import { createSelector } from "@reduxjs/toolkit";
import { adminPermissionEntityAdapter, adminRoleEntityAdapter, adminUserEntityAdapter } from "./adminSlice.js";
import { complaintEntityAdapter } from "./complaintSlice.js";
import { masterDataEntityAdapter } from "./masterDataSlice.js";
import { menuEntityAdapter, permissionEntityAdapter, roleEntityAdapter } from "./permissionSlice.js";
import { reportEntityAdapter } from "./reportSlice.js";
import type { RootState } from "./index.js";
import { getCmtsRoutePermissionForPath, isCmtsRegisteredRoutePath } from "../core/routes/cmtsRouteRegistry.js";

export const selectAuthState = (state: RootState) => state.auth;
export const selectTenantState = (state: RootState) => state.tenant;
export const selectPermissionState = (state: RootState) => state.permission;
export const selectUiState = (state: RootState) => state.ui;
export const selectMasterDataState = (state: RootState) => state.masterData;
export const selectComplaintState = (state: RootState) => state.complaint;
export const selectAdminState = (state: RootState) => state.admin;
export const selectDashboardState = (state: RootState) => state.dashboard;
export const selectReportState = (state: RootState) => state.report;
export const selectPwaState = (state: RootState) => state.pwa;

const roleSelectors = roleEntityAdapter.getSelectors((state: RootState) => state.permission.roles);
const permissionSelectors = permissionEntityAdapter.getSelectors((state: RootState) => state.permission.permissions);
const menuSelectors = menuEntityAdapter.getSelectors((state: RootState) => state.permission.menu);
const masterDataSelectors = masterDataEntityAdapter.getSelectors((state: RootState) => state.masterData.entities);
const complaintSelectors = complaintEntityAdapter.getSelectors((state: RootState) => state.complaint.complaints);
const adminUserSelectors = adminUserEntityAdapter.getSelectors((state: RootState) => state.admin.users);
const adminRoleSelectors = adminRoleEntityAdapter.getSelectors((state: RootState) => state.admin.roles);
const adminPermissionSelectors = adminPermissionEntityAdapter.getSelectors((state: RootState) => state.admin.permissions);
const reportSelectors = reportEntityAdapter.getSelectors((state: RootState) => state.report.catalog);

export const selectAuthenticatedUser = createSelector([selectAuthState], (auth) => auth.session);
export const selectAuthStatus = createSelector([selectAuthState], (auth) => auth.status);
export const selectCurrentTenantContext = createSelector([selectTenantState], (tenant) => ({
  tenantId: tenant.tenantId,
  tenantCode: tenant.tenantCode,
  tenantName: tenant.tenantName,
  timezoneCode: tenant.timezoneCode,
  orgContext: tenant.orgContext,
}));

export const selectRoles = createSelector([roleSelectors.selectAll], (roles) => roles.map((role) => role.code));
export const selectRoleEntities = roleSelectors.selectAll;
export const selectPermissions = createSelector([permissionSelectors.selectAll], (permissions) => permissions.map((permission) => permission.code));
export const selectPermissionEntities = permissionSelectors.selectAll;
export const selectEffectivePermissions = createSelector([selectPermissions], (permissions) => Array.from(new Set(permissions)).sort());
export const selectBackendMenu = menuSelectors.selectAll;

function hasNormalizedPermission(permissions: readonly string[], permission: string | undefined): boolean {
  if (!permission || permission === "PUBLIC") return true;
  const normalizedPermissions = new Set(permissions.map((item) => item.trim().toUpperCase()).filter(Boolean));
  return normalizedPermissions.has(permission.trim().toUpperCase());
}

export const selectVisibleMenuItems = createSelector(
  [selectBackendMenu, selectEffectivePermissions],
  (menuItems, permissions) => menuItems.filter((item) => {
    if (!isCmtsRegisteredRoutePath(item.href)) return false;
    const routePermission = getCmtsRoutePermissionForPath(item.href);
    return hasNormalizedPermission(permissions, routePermission) && hasNormalizedPermission(permissions, item.permission);
  }),
);

export const selectCurrentUserCapability = createSelector(
  [selectAuthenticatedUser, selectEffectivePermissions, selectRoles],
  (user, permissions, roles) => ({
    userId: user?.userId,
    displayName: user?.displayName,
    permissions,
    roles,
    canReadAllComplaints: permissions.includes("COMPLAINT.READ"),
    canAdministerUsers: permissions.includes("USER.READ") || permissions.includes("USER.CREATE"),
  }),
);

export const selectAdminUsers = adminUserSelectors.selectAll;
export const selectAdminRoles = adminRoleSelectors.selectAll;
export const selectAdminPermissions = adminPermissionSelectors.selectAll;

export const selectAdminActionAvailability = createSelector(
  [selectAdminState, selectEffectivePermissions],
  (admin, permissions) => ({
    canCreate: permissions.includes("USER.CREATE"),
    canUpdate: permissions.includes("USER.UPDATE"),
    canDelete: permissions.includes("USER.DEACTIVATE"),
    hasSelection: admin.selectedIds.length > 0,
    selectedCount: admin.selectedIds.length,
  }),
);

export const selectMasterDataEntities = masterDataSelectors.selectAll;
export const selectCurrentMasterDataEntity = createSelector([selectMasterDataState], (masterData) => masterData.currentEntityType);
export const selectSelectedMasterDataRecord = createSelector(
  [selectMasterDataState, (state: RootState) => state],
  (masterData, state) => masterData.selectedRecordId ? masterDataSelectors.selectById(state, masterData.selectedRecordId) : undefined,
);

export const selectComplaintSummaries = complaintSelectors.selectAll;
export const selectSelectedComplaint = createSelector(
  [selectComplaintState, (state: RootState) => state],
  (complaint, state) => complaint.selectedComplaintId ? complaintSelectors.selectById(state, complaint.selectedComplaintId) : undefined,
);
export const selectComplaintAllowedActions = createSelector([selectComplaintState], (complaint) => complaint.allowedActions);
export const selectDashboardFilters = createSelector([selectDashboardState], (dashboard) => dashboard.filters);
export const selectReportFilters = createSelector([selectReportState], (report) => report.filters);
export const selectReportCatalog = reportSelectors.selectAll;
export const selectCurrentReportCapability = createSelector(
  [selectReportState, selectEffectivePermissions],
  (report, permissions) => ({
    selectedReportType: report.selectedReportType,
    canExport: permissions.includes("REPORT.EXPORT"),
    canView: permissions.includes("REPORT.READ") || permissions.includes("REPORT.READ"),
  }),
);
export const selectGlobalApiError = createSelector([selectUiState], (ui) => ui.lastError);
export const selectCurrentCorrelationId = createSelector([selectUiState], (ui) => ui.lastCorrelationId);
export const selectPwaUpdateBannerState = createSelector([selectPwaState], (pwa) => ({
  updateAvailable: pwa.updateAvailable,
  visible: pwa.updateBannerVisible,
  version: pwa.currentVersion,
}));

export const selectCmtsAuth = selectAuthState;
export const selectCmtsTenant = selectTenantState;
export const selectCmtsPermissions = selectEffectivePermissions;
export const selectCmtsRoles = selectRoles;
export const selectCmtsMenuItems = selectVisibleMenuItems;
export const selectCmtsUi = selectUiState;

export const selectHasCmtsPermission = createSelector(
  [selectEffectivePermissions, (_state: RootState, permission: string) => permission],
  (permissions, permission) => permissions.includes(permission),
);

export const selectCurrentUserPermissionSet = createSelector([selectEffectivePermissions], (permissions) => new Set(permissions));
export const selectCurrentMenuForPersona = createSelector(
  [selectVisibleMenuItems, selectAuthenticatedUser],
  (menuItems, user) => menuItems.filter((item) => !user?.persona || !item.id.toLowerCase().includes("external-only") || user.persona.toLowerCase().includes("external")),
);
export const selectEffectiveTenantOrgScope = createSelector([selectCurrentTenantContext], (tenant) => ({
  tenantId: tenant.tenantId,
  tenantCode: tenant.tenantCode,
  orgUnitId: tenant.orgContext?.orgUnitId,
  siteId: tenant.orgContext?.siteId,
}));
export const selectPwaUpdateStatus = createSelector([selectPwaState], (pwa) => ({
  updateAvailable: pwa.updateAvailable,
  updateReady: pwa.updateReady,
  cacheCleanupStatus: pwa.cacheCleanupStatus,
  serviceWorkerVersion: pwa.serviceWorkerVersion,
  appVersion: pwa.appVersion,
}));
export const selectDashboardFilterState = createSelector([selectDashboardFilters], (filters) => ({ ...filters }));
export const selectReportFilterState = createSelector([selectReportFilters], (filters) => ({ ...filters }));
export const selectBackendBoundTableState = createSelector(
  [selectDashboardFilterState, selectReportFilterState],
  (dashboardFilters, reportFilters) => ({
    dashboard: dashboardFilters,
    reports: reportFilters,
    defaultPageSize: 25,
    serverPaginationRequired: true,
  }),
);
